import React from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { useOrganizationAlias } from "@domain/currentProfile/hooks";
import { ImageVersions } from "@domain/media/dtos";
import { Organization } from "@domain/organization/organization";
import { getBanner } from "@utils/banner";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { Clamp } from "@ui/components/containers/clamp";
import { row, Stack, stack } from "@ui/components/layout";
import { FollowButton } from "@ui/components/profile/followButton";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { mobile } from "@ui/styles/utils";

interface OrganizationThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	organization: Organization;
	small?: boolean;
}
export const OrganizationThumbnail: React.FC<OrganizationThumbnailProps> = ({ organization, small, ...htmlProps }) => {
	const { id, banner, avatar, type, primaryName } = organization;
	const organizationAlias = useOrganizationAlias();
	return (
		<Container {...htmlProps}>
			<Link to={Path.Organization(organization)}>
				<BackgroundImage
					backdrop
					image={banner}
					defaultUrl={!banner ? getBanner(id) : undefined}
					size={ImageVersions.MEDIUM}
				>
					<Content small={small}>
						<ProfilePicture shadowed picture={avatar} size={70} fallbackType={type} />
						<Stack gap={20}>
							<Name size={3}>{primaryName}</Name>
							{!organizationAlias && !small && <FollowButton isOrg isBig profile={organization} />}
						</Stack>
						{!organizationAlias && small && <FollowButton isOrg isBig profile={organization} />}
					</Content>
				</BackgroundImage>
			</Link>
		</Container>
	);
};

const Container = styled.div`
	position: relative;
	border-radius: 13px;
	overflow: hidden;

	> a  {
		height: 100%;
		${stack(0, "flex-start", "stretch")};
		> div  {
			flex: 1;
		}
	}
`;

const Content = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	${({ small }) =>
		small
			? css`
					${stack(20, "flex-end", "flex-start")};
					height: 100%;
				`
			: css`
					${row("M", "flex-start")};
				`}
	padding: 40px;
	@media ${mobile} {
		padding: 20px;
	}
`;

const Name = styled(Clamp)`
	${({ theme }) => theme.textStyles.desktopSubtitle};
	color: ${({ theme }) => theme.colors.white};
`;
